import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { removeOverlay as removeOverlayAction } from '../Actions/Overlay.action';
import { useTypedSelector } from '../Hooks/useTypedSelector';
import clsx from 'clsx';

const OVERLAY_CLASS = 'overlay';

const Overlay = () => {
  const dispatch = useDispatch();

  const { activeOverlays, disableOverlayClick } = useTypedSelector(
    (state) => state.overlay
  );

  const zIndexClasses = useMemo(
    () =>
      activeOverlays
        .filter((o) => !!o.zIndexClass)
        .map((o) => o.zIndexClass)
        .join(' '),
    [activeOverlays]
  );

  const removeOverlay = (componentName: string) =>
    dispatch(removeOverlayAction(componentName));

  const overlayClasses = activeOverlays
    ? activeOverlays.map((c) => `${OVERLAY_CLASS}--${c.name}`)
    : [];
  /**
   * Adds overlay classes to body to enable styling on components based on active overlays
   */
  useEffect(() => {
    // Remove all overlay classes that are not active anymore, before adding new
    const classesToRemove = Array.from(document.body.classList).filter(
      (c) => c.startsWith(OVERLAY_CLASS) || c == OVERLAY_CLASS
    );
    if (classesToRemove && classesToRemove.length > 0) {
      for (let className of classesToRemove) {
        document.body.classList.remove(className);
      }
    }

    // Add overlay classes to body
    if (activeOverlays && activeOverlays.length > 0) {
      for (let className of overlayClasses) {
        document.body.classList.add(className);
      }

      if (!document.body.style.inset) {
        document.body.style.inset = `-${window.scrollY}px 0 0`;
        document.body.style.position = 'fixed';
        document.body.style.overflowY = 'scroll';
      }
    } else if (!!document.body.style.inset) {
      const scrollY = parseFloat(
        document.body.style.inset
          .split(' ')[0]
          ?.replace('px', '')
          .replace('-', '') ?? '0'
      );
      document.body.style.inset = '';
      document.body.style.position = '';
      document.body.style.overflowY = '';
      window.scrollTo({ top: scrollY, behavior: 'auto' });
    }
  }, [activeOverlays]);

  /**
   * Removes all overlays.
   * Triggers onOverLay click on all active overlay components
   */
  const onOverlayClick = useCallback(() => {
    if (disableOverlayClick) return;

    for (let component of activeOverlays) {
      if (component.onOverlayClick) {
        if (component.onOverlayClick() !== false) {
          removeOverlay(component.name);
        }
      }
    }
  }, [activeOverlays, disableOverlayClick]);

  return (
    activeOverlays &&
    activeOverlays.length > 0 && (
      <div
        className={clsx(
          OVERLAY_CLASS,
          overlayClasses.join(' '),
          'fixed bottom-0 left-0 right-0 top-0 z-30 h-full w-full animate-[0.2s_ease-in-out_0s_both_1_fade-in] bg-overlay opacity-100',
          zIndexClasses
        )}
        onClick={onOverlayClick}
      />
    )
  );
};

export default Overlay;
